import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';

import MouseIcon from '@mui/icons-material/Mouse';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import HistoryIcon from '@mui/icons-material/History';
import { SiAnsible, SiGit, SiTerraform } from 'react-icons/si';

// ─── Определение C4 уровней и их элементов ────────────────────────────────────

const C4_LEVELS = {
  context: {
    label: 'Контекст',
    color: '#1168BD',
    border: '#0E5AA7',
    elements: [
      { key: 'person',           label: 'Person',           color: '#083F88', description: 'Пользователь системы' },
      { key: 'external-person',  label: 'External Person',  color: '#6B6B6B', description: 'Внешний пользователь' },
      { key: 'system',           label: 'System',           color: '#1168BD', description: 'Программная система' },
      { key: 'external-system',  label: 'External System',  color: '#999999', description: 'Внешняя система' },
    ],
  },
  container: {
    label: 'Контейнер',
    color: '#438DD5',
    border: '#3A7BB8',
    elements: [
      { key: 'container',        label: 'Container',        color: '#438DD5', description: 'Приложение / сервис' },
      { key: 'database',         label: 'Database',         color: '#438DD5', description: 'База данных' },
      { key: 'queue',            label: 'Message Queue',    color: '#438DD5', description: 'Очередь сообщений' },
      { key: 'webapp',           label: 'Web App',          color: '#438DD5', description: 'Веб-приложение' },
      { key: 'mobileapp',        label: 'Mobile App',       color: '#438DD5', description: 'Мобильное приложение' },
      { key: 'filestore',        label: 'File Store',       color: '#438DD5', description: 'Файловое хранилище' },
      { key: 'microservice',     label: 'Microservice',     color: '#438DD5', description: 'Микросервис' },
      { key: 'serverless',       label: 'Serverless Fn',    color: '#438DD5', description: 'Бессерверная функция' },
    ],
  },
  component: {
    label: 'Компонент',
    color: '#85BBF0',
    border: '#78AAD4',
    elements: [
      { key: 'component',        label: 'Component',        color: '#85BBF0', description: 'Компонент' },
      { key: 'controller',       label: 'Controller',       color: '#85BBF0', description: 'Контроллер' },
      { key: 'repository',       label: 'Repository',       color: '#85BBF0', description: 'Репозиторий' },
      { key: 'service',          label: 'Service',          color: '#85BBF0', description: 'Сервис' },
      { key: 'facade',           label: 'Facade',           color: '#85BBF0', description: 'Фасад' },
    ],
  },
  code: {
    label: 'Код',
    color: '#C9E8FF',
    border: '#5A9FD4',
    elements: [
      { key: 'class',            label: 'Class',            color: '#C9E8FF', description: 'Класс' },
      { key: 'interface',        label: 'Interface',        color: '#C9E8FF', description: 'Интерфейс' },
      { key: 'enum',             label: 'Enum',             color: '#C9E8FF', description: 'Перечисление' },
      { key: 'abstract-class',   label: 'Abstract Class',   color: '#C9E8FF', description: 'Абстрактный класс' },
    ],
  },
};

// ─── Кнопка тулбара ──────────────────────────────────────────────────────────

const ToolbarButton = ({ title, onClick, active, children, style = {} }) => {
  const { theme } = useTheme();
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        backgroundColor: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
        color: theme.toolbar.buttonText,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.toolbar.buttonBorder,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '14px',
        minWidth: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        ...style,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground; }}
    >
      {children}
    </button>
  );
};

// ─── Цветная метка уровня ────────────────────────────────────────────────────

const LevelDot = ({ color, border }) => (
  <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: color, borderWidth: '1px', borderStyle: 'solid', borderColor: border, flexShrink: 0 }} />
);

// ─── Основной тулбар C4 ──────────────────────────────────────────────────────

const ToolbarC4 = ({
  tool,
  setTool,
  zoomIn,
  zoomOut,
  fitToBoard,
  centerToSelection,
  onSaveDiagram,
  onLoadDiagram,
  currentDiagramId,
  onPublishDiagram,
  isPublishing = false,
  onShowHistory,
  diagramGitlabPath,
  elements = [],
  topOffset = 0,
  onExportImage,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  showCodePanel,
  setShowCodePanel,
  onSyncToCode,
  onExportTerraform,
  onExportAnsible,
  onBeforeNavigate,
}) => {
  const { theme, toggleTheme, currentTheme } = useTheme();
  const navigate = useNavigate();

  const [selectedLevel, setSelectedLevel] = useState('context');
  const [showLevelMenu, setShowLevelMenu] = useState(false);
  const [showElementMenu, setShowElementMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const levelMenuRef = useRef(null);
  const elementMenuRef = useRef(null);
  const exportMenuRef = useRef(null);

  const iconStyle = { width: 18, height: 18 };
  const guardedNavigate = (target) => {
    if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
    navigate(target);
  };
  const level = C4_LEVELS[selectedLevel];
  const levelElements = level.elements;

  // Закрытие выпадающих меню при клике вне
  useEffect(() => {
    const handleOutside = (e) => {
      if (levelMenuRef.current && !levelMenuRef.current.contains(e.target)) setShowLevelMenu(false);
      if (elementMenuRef.current && !elementMenuRef.current.contains(e.target)) setShowElementMenu(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Инструмент из текущего элемента: "c4-person", "c4-system" и т.д.
  const toolForKey = (key) => `c4-${key}`;
  const isActive = (key) => tool === toolForKey(key);

  const handleSelectElement = (key) => {
    setTool(toolForKey(key));
    setShowElementMenu(false);
  };

  // Находим активный элемент для отображения в кнопке
  const activeElementDef = Object.values(C4_LEVELS)
    .flatMap(l => l.elements)
    .find(el => tool === toolForKey(el.key));

  const Divider = () => (
    <div style={{ width: 1, height: 24, background: theme.toolbar.border, margin: '0 4px' }} />
  );

  return (
    <div style={{
      position: 'absolute',
      top: topOffset,
      left: 0,
      right: 0,
      height: 56,
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.toolbar.background,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: theme.toolbar.border,
      padding: '0 16px',
      boxShadow: theme.toolbar.shadow,
    }}>

      {/* ── Левая секция ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

        {/* Назад */}
        <ToolbarButton title="Назад" onClick={() => guardedNavigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{ fontSize: 17, fontWeight: 'bold', color: theme.toolbar.text, marginRight: 8 }}>
          C4 Model
        </div>

        {/* Сохранить */}
        <ToolbarButton title="Сохранить диаграмму (Ctrl+S)" onClick={onSaveDiagram}>
          <SaveIcon style={iconStyle} />
        </ToolbarButton>

        {/* Опубликовать в GitLab */}
        {onPublishDiagram && (
          <ToolbarButton
            title={isPublishing ? 'Публикуется...' : 'Опубликовать в GitLab'}
            onClick={onPublishDiagram}
            style={{ color: isPublishing ? undefined : '#f05032', borderColor: isPublishing ? undefined : '#f05032', opacity: isPublishing ? 0.6 : 1 }}
          >
            <SiGit style={iconStyle} />
          </ToolbarButton>
        )}

        {onShowHistory && diagramGitlabPath && (
          <ToolbarButton
            title="История версий"
            onClick={onShowHistory}
            style={{ color: '#a6e3a1', borderColor: '#40a02b' }}
          >
            <HistoryIcon style={iconStyle} />
          </ToolbarButton>
        )}

        {/* Экспорт изображения */}
        {onExportImage && (
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <ToolbarButton title="Экспорт изображения" onClick={() => setShowExportMenu(!showExportMenu)} active={showExportMenu}>
              <ImageIcon style={iconStyle} />
            </ToolbarButton>
            {showExportMenu && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 100,
              }}>
                {['png', 'jpg', 'svg'].map(fmt => (
                  <button key={fmt} onClick={() => { onExportImage(fmt); setShowExportMenu(false); }}
                    style={{
                      display: 'block', width: '100%', padding: '8px 16px',
                      backgroundColor: 'transparent', border: 'none',
                      color: theme.toolbar.buttonText, textAlign: 'left',
                      cursor: 'pointer', textTransform: 'uppercase', fontSize: 12,
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >{fmt}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <Divider />

        {/* Undo / Redo */}
        <ToolbarButton title="Отменить (Ctrl+Z)" onClick={() => canUndo && onUndo?.()}
          style={{ opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'not-allowed' }}>
          <UndoIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton title="Повторить (Ctrl+Y)" onClick={() => canRedo && onRedo?.()}
          style={{ opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'not-allowed' }}>
          <RedoIcon style={iconStyle} />
        </ToolbarButton>

        <Divider />

        {/* Инструмент выбора */}
        <ToolbarButton title="Выбор (S)" onClick={() => setTool('select')} active={tool === 'select'}>
          <MouseIcon style={iconStyle} />
        </ToolbarButton>

        {/* Связь */}
        <ToolbarButton title="Связь (A)" onClick={() => setTool('arrow')} active={tool === 'arrow'}>
          <ArrowForwardIcon style={iconStyle} />
        </ToolbarButton>

        <Divider />

        {/* Выбор уровня C4 */}
        <div style={{ position: 'relative' }} ref={levelMenuRef}>
          <ToolbarButton
            title="Выбрать уровень C4"
            onClick={() => setShowLevelMenu(!showLevelMenu)}
            active={showLevelMenu}
            style={{ minWidth: 130, backgroundColor: level.color, borderColor: level.border, color: '#fff' }}
          >
            <LevelDot color={level.color} border={level.border} />
            <span style={{ color: '#fff' }}>{level.label}</span>
            <ExpandMoreIcon style={{ width: 16, height: 16, color: '#fff' }} />
          </ToolbarButton>
          {showLevelMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4,
              background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000, minWidth: 160,
            }}>
              {Object.entries(C4_LEVELS).map(([key, lvl]) => (
                <button key={key}
                  onClick={() => { setSelectedLevel(key); setShowLevelMenu(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '8px 12px',
                    backgroundColor: selectedLevel === key ? lvl.color + '33' : 'transparent',
                    border: 'none', color: theme.toolbar.buttonText,
                    textAlign: 'left', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = lvl.color + '33'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedLevel === key ? lvl.color + '33' : 'transparent'}
                >
                  <LevelDot color={lvl.color} border={lvl.border} />
                  {lvl.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Выбор элемента уровня */}
        <div style={{ position: 'relative' }} ref={elementMenuRef}>
          <ToolbarButton
            title="Выбрать элемент C4"
            onClick={() => setShowElementMenu(!showElementMenu)}
            active={showElementMenu || !!activeElementDef}
            style={{
              minWidth: 170,
              ...(activeElementDef ? { backgroundColor: activeElementDef.color + '33', borderColor: activeElementDef.color } : {}),
            }}
          >
            {activeElementDef ? (
              <>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: activeElementDef.color, flexShrink: 0 }} />
                <span>{activeElementDef.label}</span>
              </>
            ) : (
              <span>Элементы ({levelElements.length})</span>
            )}
            <ExpandMoreIcon style={{ width: 16, height: 16 }} />
          </ToolbarButton>
          {showElementMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4,
              background: theme.toolbar.background, border: `1px solid ${theme.toolbar.border}`,
              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 3000,
              minWidth: 240, maxHeight: 400, overflowY: 'auto',
            }}>
              {levelElements.map((el) => {
                const active = isActive(el.key);
                return (
                  <button key={el.key} onClick={() => handleSelectElement(el.key)}
                    title={el.description}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 12px',
                      backgroundColor: active ? el.color + '44' : 'transparent',
                      border: 'none', color: theme.toolbar.buttonText,
                      textAlign: 'left', cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = el.color + '33'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = active ? el.color + '44' : 'transparent'}
                  >
                    <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: el.color, borderWidth: '1px', borderStyle: 'solid', borderColor: level.border, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: active ? 600 : 400 }}>{el.label}</div>
                      <div style={{ fontSize: 10, opacity: 0.6 }}>{el.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Центральная секция — навигация ───────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ToolbarButton title="Увеличить (+)" onClick={zoomIn}><ZoomInIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton title="Уменьшить (-)" onClick={zoomOut}><ZoomOutIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton title="Вместить всё (0)" onClick={fitToBoard}><CenterFocusStrongIcon style={iconStyle} /></ToolbarButton>
        <ToolbarButton title="К выделенному" onClick={centerToSelection}><GpsFixedIcon style={iconStyle} /></ToolbarButton>
      </div>

      {/* ── Правая секция ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {onSyncToCode && (
          <ToolbarButton
            title="Синхронизировать холст → DSL код"
            onClick={onSyncToCode}
            style={{ fontSize: 11, padding: '6px 10px', gap: 4 }}
          >
            ← Sync to Code
          </ToolbarButton>
        )}
        {onExportTerraform && (
          <ToolbarButton
            title="Скачать Terraform main.tf для Timeweb Cloud"
            onClick={onExportTerraform}
            style={{ color: '#7b42bc', borderColor: '#7b42bc' }}
          >
            <SiTerraform style={iconStyle} />
          </ToolbarButton>
        )}
        {onExportAnsible && (
          <ToolbarButton
            title="Скачать Ansible playbook.yml и inventory.ini для Kubernetes"
            onClick={onExportAnsible}
            style={{ color: '#ee0000', borderColor: '#ee0000' }}
          >
            <SiAnsible style={iconStyle} />
          </ToolbarButton>
        )}
        {setShowCodePanel && (
          <ToolbarButton
            title={showCodePanel ? 'Скрыть код' : 'Показать код'}
            onClick={() => setShowCodePanel(!showCodePanel)}
            active={showCodePanel}
          >
            <CodeIcon style={iconStyle} />
          </ToolbarButton>
        )}
        <ToolbarButton title={`Тема: ${currentTheme}`} onClick={toggleTheme}>
          {currentTheme === 'dark' ? <LightModeIcon style={iconStyle} /> : <DarkModeIcon style={iconStyle} />}
        </ToolbarButton>
        {elements.length > 0 && (
          <div style={{ color: theme.toolbar.text, fontSize: 12, opacity: 0.7, marginLeft: 4 }}>
            {elements.length} эл. • C4 Model
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolbarC4;
